// Network calculation utilities for ZeroConf

/**
 * Convert IP address string to 32-bit integer
 */
export function ipToNumber(ip: string): number {
  const parts = ip.split('.').map(Number);
  return (parts[0] << 24) + (parts[1] << 16) + (parts[2] << 8) + parts[3];
}

/**
 * Convert 32-bit integer to IP address string
 */
export function numberToIp(num: number): string {
  return [
    (num >>> 24) & 255,
    (num >>> 16) & 255,
    (num >>> 8) & 255,
    num & 255,
  ].join('.');
}

/**
 * Calculate network address from IP and subnet mask
 */
export function getNetworkAddress(ip: string, mask: string): string {
  const ipNum = ipToNumber(ip);
  const maskNum = ipToNumber(mask);
  return numberToIp(ipNum & maskNum);
}

/**
 * Calculate broadcast address from IP and subnet mask
 */
export function getBroadcastAddress(ip: string, mask: string): string {
  const ipNum = ipToNumber(ip);
  const maskNum = ipToNumber(mask);
  const invertedMask = ~maskNum >>> 0;
  return numberToIp((ipNum & maskNum) | invertedMask);
}

/**
 * Get number of usable hosts in a subnet
 */
export function getUsableHosts(mask: string): number {
  const maskNum = ipToNumber(mask);
  const invertedMask = ~maskNum >>> 0;
  const totalHosts = invertedMask + 1;
  return Math.max(0, totalHosts - 2); // Subtract network and broadcast
}

/**
 * Convert subnet mask to CIDR notation
 */
export function maskToCIDR(mask: string): number {
  const maskNum = ipToNumber(mask);
  let cidr = 0;
  let tempMask = maskNum;
  while (tempMask & 0x80000000) {
    cidr++;
    tempMask = (tempMask << 1) >>> 0;
  }
  return cidr;
}

/**
 * Convert CIDR to subnet mask
 */
export function cidrToMask(cidr: number): string {
  const mask = cidr === 0 ? 0 : (~0 << (32 - cidr)) >>> 0;
  return numberToIp(mask);
}

/**
 * Check if two subnets overlap
 */
export function subnetsOverlap(
  ip1: string, mask1: string,
  ip2: string, mask2: string
): boolean {
  const net1Start = ipToNumber(getNetworkAddress(ip1, mask1));
  const net1End = ipToNumber(getBroadcastAddress(ip1, mask1));
  const net2Start = ipToNumber(getNetworkAddress(ip2, mask2));
  const net2End = ipToNumber(getBroadcastAddress(ip2, mask2));
  
  return !(net1End < net2Start || net2End < net1Start);
}

/**
 * Calculate smart DHCP range excluding network, broadcast, and gateway
 */
export function calculateDHCPRange(
  gatewayIp: string,
  subnetMask: string,
  excludeIps: string[] = []
): { start: string; end: string; available: number } | null {
  try {
    const networkAddr = getNetworkAddress(gatewayIp, subnetMask);
    const broadcastAddr = getBroadcastAddress(gatewayIp, subnetMask);
    
    const networkNum = ipToNumber(networkAddr);
    const broadcastNum = ipToNumber(broadcastAddr);
    const gatewayNum = ipToNumber(gatewayIp);
    
    // Create set of excluded IPs
    const excluded = new Set<number>();
    excluded.add(networkNum);      // Network address
    excluded.add(broadcastNum);    // Broadcast address
    excluded.add(gatewayNum);      // Gateway
    excludeIps.forEach(ip => excluded.add(ipToNumber(ip)));
    
    // Find first available IP
    let startNum = networkNum + 1;
    while (excluded.has(startNum) && startNum < broadcastNum) {
      startNum++;
    }
    
    // Find last available IP
    let endNum = broadcastNum - 1;
    while (excluded.has(endNum) && endNum > startNum) {
      endNum--;
    }
    
    if (startNum >= endNum) {
      return null; // No usable range
    }
    
    // Count available IPs
    let available = 0;
    for (let i = startNum; i <= endNum; i++) {
      if (!excluded.has(i)) available++;
    }
    
    return {
      start: numberToIp(startNum),
      end: numberToIp(endNum),
      available,
    };
  } catch {
    return null;
  }
}

/**
 * Validate if an IP is within a subnet
 */
export function isIpInSubnet(ip: string, networkIp: string, mask: string): boolean {
  const ipNum = ipToNumber(ip);
  const networkNum = ipToNumber(getNetworkAddress(networkIp, mask));
  const broadcastNum = ipToNumber(getBroadcastAddress(networkIp, mask));
  return ipNum >= networkNum && ipNum <= broadcastNum;
}

/**
 * Get subnet information summary
 */
export function getSubnetInfo(ip: string, mask: string): {
  network: string;
  broadcast: string;
  firstUsable: string;
  lastUsable: string;
  usableHosts: number;
  cidr: number;
} | null {
  try {
    const network = getNetworkAddress(ip, mask);
    const broadcast = getBroadcastAddress(ip, mask);
    const networkNum = ipToNumber(network);
    const broadcastNum = ipToNumber(broadcast);
    
    return {
      network,
      broadcast,
      firstUsable: numberToIp(networkNum + 1),
      lastUsable: numberToIp(broadcastNum - 1),
      usableHosts: getUsableHosts(mask),
      cidr: maskToCIDR(mask),
    };
  } catch {
    return null;
  }
}

// Common subnet masks reference
export const COMMON_SUBNETS = [
  { mask: '255.255.255.252', cidr: 30, hosts: 2 },
  { mask: '255.255.255.248', cidr: 29, hosts: 6 },
  { mask: '255.255.255.240', cidr: 28, hosts: 14 },
  { mask: '255.255.255.224', cidr: 27, hosts: 30 },
  { mask: '255.255.255.192', cidr: 26, hosts: 62 },
  { mask: '255.255.255.128', cidr: 25, hosts: 126 },
  { mask: '255.255.255.0', cidr: 24, hosts: 254 },
  { mask: '255.255.254.0', cidr: 23, hosts: 510 },
  { mask: '255.255.252.0', cidr: 22, hosts: 1022 },
  { mask: '255.255.248.0', cidr: 21, hosts: 2046 },
  { mask: '255.255.240.0', cidr: 20, hosts: 4094 },
  { mask: '255.255.0.0', cidr: 16, hosts: 65534 },
];

// Private IP ranges
export const PRIVATE_IP_RANGES = [
  { class: 'A', range: '10.0.0.0 - 10.255.255.255', cidr: '10.0.0.0/8' },
  { class: 'B', range: '172.16.0.0 - 172.31.255.255', cidr: '172.16.0.0/12' },
  { class: 'C', range: '192.168.0.0 - 192.168.255.255', cidr: '192.168.0.0/16' },
];
